import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { ActionMenu, Chevron, MUTED, type ActionMenuSection } from "@majourneys/ui";
import { latnLocale } from "../../../lib/format";
import { ChatToolbarButton } from "./chat-toolbar";
import { conversationRequestSent, type StoredConversation } from "../hooks/use-chat-history";

interface ChatHistoryMenuProps {
  conversations: readonly StoredConversation[];
  activeId: string | null;
  onNewChat: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * The "New chat / History" toolbar shared between the homepage modal and the
 * in-section Discover chat. Lifted out of either chat surface so a change to
 * history UX ships to both in one edit (per the ai-chat-parity rule).
 *
 * The menu is `@majourneys/ui`'s action menu now: rows that do something rather
 * than mean something, each with its own delete handle beside it rather than
 * inside it, and one flat run for the arrow keys. What it gains over the hand
 * copy it replaces: arrow keys at all, Home and End, Escape that stops here
 * instead of travelling on to the modal behind, and focus back on the button
 * that opened it.
 *
 * And the delete asks in the row. It used to call `window.confirm`, which drops
 * a grey browser dialog over the middle of the concierge, cannot be styled,
 * cannot be translated, and on a phone covers the thing it is asking about.
 */
export function ChatHistoryMenu({
  conversations,
  activeId,
  onNewChat,
  onSelect,
  onDelete,
}: ChatHistoryMenuProps) {
  const { t, i18n } = useTranslation();
  const newChatLabel = t("ai_concierge.new_chat", { defaultValue: "New chat" });
  const historyLabel = t("ai_concierge.history", { defaultValue: "History" });
  const requestSentLabel = t("ai_concierge.history_request_sent", { defaultValue: "Request sent" });

  const sections = useMemo<ActionMenuSection[]>(() => {
    const recent = conversations.slice(0, 12);
    if (recent.length === 0) return [];
    return [
      {
        id: "recent",
        items: recent.map((c) => {
          // C30: a chat that led to a request the guest really sent says so,
          // on proof only (the same `requestSent` the turn's card reads).
          const sent = conversationRequestSent(c);
          const when = formatRelative(c.updatedAt, i18n.language);
          return {
          id: c.id,
          title: c.title,
          meta: sent ? `${requestSentLabel} · ${when}` : when,
          lead: sent ? (
            <CheckCircle2 size={14} color="#1A7A44" aria-hidden="true" data-testid="history-request-sent" />
          ) : (
            <MessageCircle size={14} color={MUTED} aria-hidden="true" />
          ),
          current: c.id === activeId,
          onSelect: () => onSelect(c.id),
          remove: {
            label: t("ai_concierge.history_delete", { defaultValue: "Delete" }),
            question: t("ai_concierge.history_delete_confirm", {
              defaultValue: "Delete this conversation?",
            }),
            confirmLabel: t("ai_concierge.history_delete_yes", { defaultValue: "Delete" }),
            cancelLabel: t("ai_concierge.history_delete_no", { defaultValue: "Keep" }),
            onConfirm: () => onDelete(c.id),
          },
          };
        }),
      },
    ];
  }, [conversations, activeId, onSelect, onDelete, t, i18n.language, requestSentLabel]);

  // A fragment, not a row: these are two segments of the header's one
  // toolbar capsule (`ChatToolbar`), which owns the ring and the dividers.
  return (
    <>
      <ChatToolbarButton
        onClick={onNewChat}
        label={newChatLabel}
        icon={<Icon name="ri-add-line" size={12} aria-hidden="true" />}
      />

      <ActionMenu
        name="chat-history"
        sections={sections}
        width={320}
        labels={{
          title: historyLabel,
          closeList: t("common.menu_close_list", { defaultValue: "Close the list" }),
          emptyTitle: t("ai_concierge.history_empty", {
            defaultValue: "No previous chats yet.",
          }),
        }}
        renderTrigger={({ ref, open, buttonProps }) => (
          <ChatToolbarButton
            rest={buttonProps as Record<string, unknown>}
            buttonRef={ref}
            label={historyLabel}
            icon={<Icon name="ri-time-line" size={12} aria-hidden="true" />}
            trailing={
              <>
                <span style={{ fontSize: 10, color: MUTED, fontVariantNumeric: "tabular-nums" }}>
                  {conversations.length}
                </span>
                <Chevron open={open} size={14} />
              </>
            }
          />
        )}
      />
    </>
  );
}

/**
 * "5 min ago" in the chat's language, Latin digits in every locale. Was
 * hard-coded English ("5m ago") with a device-locale date fallback.
 */
function formatRelative(ts: number, lang: string): string {
  const locale = latnLocale(lang);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return rtf.format(0, "second");
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 7) return rtf.format(-days, "day");
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return rtf.format(-weeks, "week");
  return new Date(ts).toLocaleDateString(locale);
}
